/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import type { ChangeEvent, ChangeEventHandler, ComponentProps, ReactNode } from 'react'
import { forwardRef, useId } from 'react'
import type { RegisterOptions } from 'react-hook-form'
import { useFormContext, useFormState } from 'react-hook-form'

import { cn, mergeRefs } from '../../../lib/utils'
import { Field, FieldDescription, FieldError, FieldLabel } from '../../ui/field'
import { NativeSelect } from '../../ui/native-select'

export type NativeSelectFormFieldProps = {
  autoComplete?: ComponentProps<'select'>['autoComplete']
  children?: ReactNode
  disabled?: boolean
  /** When true, the label sits on the top border of the control (notch style). Ignored if `label` is omitted. */
  floatingLabel?: boolean
  fieldProps?: ComponentProps<typeof Field>
  helperText?: string
  label?: string
  name: string
  onValueChange?: ChangeEventHandler<HTMLSelectElement>
  placeholder?: string
  required?: boolean
  selectProps?: Omit<ComponentProps<typeof NativeSelect>, 'size'>
  validation?: RegisterOptions
}

export const NativeSelectFormField = forwardRef<HTMLSelectElement, NativeSelectFormFieldProps>(
  (props, forwardedRef) => {
    const { register, control } = useFormContext()
    const { errors } = useFormState({ control, name: props.name })

    const { ref, ...workRegister } = register(props.name, props.validation)

    const generatedId = useId()
    const controlId = props.selectProps?.id ?? props.fieldProps?.id ?? generatedId

    const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
      workRegister.onChange(e)
      props?.onValueChange?.(e)
    }

    const error = errors[props.name]
    const invalid = !!error

    const { selectProps } = props
    const { ref: selectRef, className: selectClassName, id: _selectId, ...restSelectProps } = selectProps ?? {}

    return (
      <Field {...props.fieldProps} data-invalid={invalid}>
        {props.label !== undefined && !props.floatingLabel && (
          <FieldLabel className="flex items-center gap-1" htmlFor={controlId}>
            {props.label}
            {props.required ? (
              <span aria-hidden className="text-destructive">
                *
              </span>
            ) : null}
          </FieldLabel>
        )}
        <div className={cn('relative w-full', props.floatingLabel && props.label !== undefined && 'pt-2')}>
          {props.label !== undefined && props.floatingLabel && (
            <FieldLabel
              className="pointer-events-auto absolute left-2 z-10 inline-flex w-fit -translate-y-1/2 gap-1 bg-background px-1 text-xs leading-none"
              htmlFor={controlId}
            >
              {props.label}
              {props.required ? (
                <span aria-hidden className="text-destructive">
                  *
                </span>
              ) : null}
            </FieldLabel>
          )}
          <NativeSelect
            {...restSelectProps}
            {...workRegister}
            aria-invalid={invalid}
            autoComplete={props.autoComplete}
            className={cn('w-full', selectClassName)}
            disabled={props.disabled}
            id={controlId}
            onChange={handleChange}
            ref={mergeRefs(ref, forwardedRef, selectRef)}
            required={props.required}
            suppressHydrationWarning
          >
            {props.placeholder !== undefined ? <option value="">{props.placeholder}</option> : null}
            {props.children}
          </NativeSelect>
        </div>
        {props.helperText !== undefined ? <FieldDescription>{props.helperText}</FieldDescription> : null}
        {error?.message ? <FieldError>{String(error.message)}</FieldError> : null}
      </Field>
    )
  }
)

NativeSelectFormField.displayName = 'NativeSelectFormField'
